import React, { useRef, useEffect, useCallback, useState, useMemo } from "react";
import PropTypes from "prop-types";
import Breadcrumb from "./Breadcrumb";
import FeedbackCard from "./FeedbackCard";
import { buildScopeTranslate } from "lib/I18n";
import { GRADE_LABEL_KEYS } from "../constants";
import { refPropType } from "../utils";

const tQuestion = buildScopeTranslate("views.content.agentforce.assessments.pages.question");
const tLabels = buildScopeTranslate("views.content.agentforce.assessments.labels");

// Max characters allowed per open-ended answer (W-21285114)
const MAX_ANSWER_LENGTH = 2000;

const MIN_ANSWER_LENGTH = 12;

const FUN_MESSAGE_KEYS = [
  "loading_messages.taking_a_look",
  "loading_messages.interesting_take",
  "loading_messages.evaluating",
  "loading_messages.almost_there",
  "loading_messages.comparing_notes",
];

const ROTATION_INTERVAL_MS = 2500;
const SLOW_THRESHOLD_MS = 10000;

function shuffleArray(arr) {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function useRotatingMessage(active) {
  const [phase, setPhase] = useState("initial");
  const [funIndex, setFunIndex] = useState(0);
  const shuffledKeys = useMemo(
    () => (active ? shuffleArray(FUN_MESSAGE_KEYS) : FUN_MESSAGE_KEYS),
    [active]
  );

  useEffect(() => {
    if (!active) {
      setPhase("initial");
      setFunIndex(0);
      return;
    }

    const firstRotation = setTimeout(() => {
      setPhase("rotating");
    }, ROTATION_INTERVAL_MS);

    const slowTimer = setTimeout(() => {
      setPhase("slow");
    }, SLOW_THRESHOLD_MS);

    return () => {
      clearTimeout(firstRotation);
      clearTimeout(slowTimer);
    };
  }, [active, shuffledKeys]);

  useEffect(() => {
    if (!active || phase !== "rotating") return;

    const interval = setInterval(() => {
      setFunIndex((prev) => (prev + 1) % shuffledKeys.length);
    }, ROTATION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [active, phase, shuffledKeys]);

  if (!active) return "";
  if (phase === "slow") return tQuestion("loading_message_slow");
  if (phase === "initial") return tQuestion("loading_text");
  return tQuestion(shuffledKeys[funIndex]);
}

const SubmitSection = ({ onSubmitAnswer, answerText }) => {
  const isTooShort = answerText.trim().length < MIN_ANSWER_LENGTH;

  return (
    <div className="slds-m-top_large">
      <div className="slds-grid slds-grid_align-end">
        <tds-button
          variant="brand"
          disabled={isTooShort}
          data-testid="question-submit-btn"
          onClick={onSubmitAnswer}>
          <span>{tQuestion("buttons.primary")}</span>
        </tds-button>
      </div>
    </div>
  );
};

SubmitSection.propTypes = {
  onSubmitAnswer: PropTypes.func.isRequired,
  answerText: PropTypes.string.isRequired,
};

// Handles both grading-in-progress and answered states
const PostSubmitSection = ({ submitting, response = null, isCompleted, onNextQuestion, feedbackHeadingRef = null }) => {
  const loadingMessage = useRotatingMessage(submitting);
  const gradeLabelKey = response ? GRADE_LABEL_KEYS[response.agentGrade] : null;
  const gradeLabel = gradeLabelKey ? tLabels(gradeLabelKey) : (response ? response.agentGrade : '');

  return (
    <div>
      <div aria-live="assertive" className="th-sr-only" data-testid="grading-live-region">
        {submitting ? loadingMessage : ""}
      </div>
      <div aria-live="polite" className="th-sr-only" data-testid="grade-announcement">
        {!submitting && response ? gradeLabel : ""}
      </div>

      {/* Next button — right-aligned, disabled while grading */}
      <div className="slds-grid slds-grid_align-end slds-m-top_medium slds-m-bottom_medium">
        <tds-button
          variant="brand"
          disabled={submitting}
          onClick={onNextQuestion}
          data-testid="question-next-btn"
          {...(isCompleted ? { 'aria-description': tQuestion("all_questions_answered") } : {})}>
          <span>{isCompleted ? tQuestion("buttons.view_results") : tQuestion("buttons.next_question")}</span>
        </tds-button>
      </div>

      {/* Loading indicator — centered, shown while grading */}
      {submitting && (
        <div className="slds-grid slds-grid_vertical-align-center slds-grid_align-center th-agentforce-assessment__loading-indicator">
          <tds-spinner size="small" aria-hidden="true"></tds-spinner>
          <span className="slds-text-heading_small" data-testid="grading-message">{loadingMessage}</span>
        </div>
      )}

      {/* Feedback card — full width, shown after grading */}
      {!submitting && response && (
        <FeedbackCard response={response} gradeRef={feedbackHeadingRef} />
      )}
    </div>
  );
};

PostSubmitSection.propTypes = {
  submitting: PropTypes.bool.isRequired,
  response: PropTypes.object,
  isCompleted: PropTypes.bool.isRequired,
  onNextQuestion: PropTypes.func.isRequired,
  feedbackHeadingRef: refPropType,
};

const QuestionScreen = ({
  attempt,
  currentQuestion,
  currentQuestionIndex,
  answerText,
  submitting,
  onAnswerChange,
  onSubmitAnswer,
  onNextQuestion,
  onBackToTrail,
  onInfoClick,
  onFeedbackClick,
  showFeedbackButton,
}) => {
  const textareaRef = useRef(null);
  const feedbackHeadingRef = useRef(null);

  // Calculate derived values before early return
  const isAnswered = currentQuestion ? Boolean(currentQuestion.response) : false;
  const isCompleted = attempt ? attempt.status === "COMPLETED" : false;
  const answerValue = isAnswered && currentQuestion ? currentQuestion.response.userResponse : answerText;
  const totalQuestions = attempt?.questions?.length || 0;

  // Auto-focus the textarea when a new unanswered question loads (a11y audit approved:
  // the label + aria-describedby give screen readers full context without extra navigation)
  useEffect(() => {
    if (!isAnswered && currentQuestion && textareaRef.current) {
      const timer = setTimeout(() => {
        textareaRef.current.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentQuestion?.quizQuestionId, isAnswered]);

  // Focus the feedback card grade heading when the answer has been graded
  useEffect(() => {
    if (isAnswered) {
      const timer = setTimeout(() => {
        const feedbackHeading = feedbackHeadingRef.current;
        if (feedbackHeading && typeof feedbackHeading.focus === 'function') {
          feedbackHeading.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isAnswered]);

  const showTruncationToast = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent("showToast", {
        detail: {
          heading: tQuestion("text_truncated", { limit: MAX_ANSWER_LENGTH }),
          variant: "warning",
        },
      })
    );
  }, []);

  const handleAnswerChange = (text) => {
    onAnswerChange(text);
  };

  const handlePaste = (e) => {
    if (isAnswered || submitting) return;

    e.preventDefault();
    const pasted = e.clipboardData.getData('text/plain');
    const { selectionStart, selectionEnd, value } = e.target;
    const before = value.substring(0, selectionStart);
    const after = value.substring(selectionEnd);
    const combined = before + pasted + after;
    const truncated = combined.substring(0, MAX_ANSWER_LENGTH);

    if (combined.length > MAX_ANSWER_LENGTH) {
      showTruncationToast();
    }

    onAnswerChange(truncated);
  };

  const handleSubmitAnswer = (e) => {
    e.preventDefault();
    if (submitting || answerText.trim().length < MIN_ANSWER_LENGTH || answerText.length > MAX_ANSWER_LENGTH) {
      return;
    }
    onSubmitAnswer(e);
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      if (!isAnswered && !submitting && answerText.trim().length >= MIN_ANSWER_LENGTH && answerText.length <= MAX_ANSWER_LENGTH) {
        onSubmitAnswer(e);
      }
    }
  };

  // Early return after all hooks have been called
  if (!attempt || !currentQuestion) {
    return null;
  }

  return (
    <>
      <Breadcrumb
        showProgress={true}
        currentQuestionIndex={currentQuestionIndex}
        totalQuestions={totalQuestions}
        onBackClick={onBackToTrail}
        onInfoClick={onInfoClick}
        onFeedbackClick={onFeedbackClick}
        showFeedbackButton={showFeedbackButton}
      />

      {/* Question Content */}
      <div className="slds-container_center slds-container_medium slds-p-around_large">
        <h1 className="th-sr-only" tabIndex="-1" data-testid="question-screen-heading">
          {tQuestion("question_x_of_y", { number: currentQuestionIndex + 1, total: totalQuestions })}
        </h1>
        <div className="slds-text-align_left slds-m-bottom_medium" aria-hidden="true">
          <tds-badge>
            <span>{tQuestion("question_label", { number: currentQuestionIndex + 1 })}</span>
          </tds-badge>
        </div>

        <div className="slds-text-align_left slds-m-bottom_large">
          <h2
            id="question-text"
            className="tds-color_black slds-text-heading_medium tds-text_bold"
            data-testid="question-text"
            tabIndex="0">
            {currentQuestion.questionText}
          </h2>
        </div>

        <form onSubmit={handleSubmitAnswer}>
          <div className="th-agentforce-assessment__question-textarea slds-form-element">
            <label className="slds-form-element__label" htmlFor="question-answer-input">
              {tQuestion("answer_label")}
            </label>
            <div className="slds-form-element__control">
              <textarea
                ref={textareaRef}
                id="question-answer-input"
                className="slds-textarea"
                key={currentQuestion.quizQuestionId}
                value={answerValue}
                disabled={isAnswered || submitting}
                required={!isAnswered}
                tabIndex={isAnswered ? -1 : undefined}
                rows={6}
                maxLength={MAX_ANSWER_LENGTH}
                placeholder={tQuestion("answer_helper")}
                data-testid="question-answer-input"
                aria-describedby="question-text answer-char-counter"
                onChange={(e) => handleAnswerChange(e.target.value)}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
              />
            </div>
          </div>

          {!isAnswered && !submitting && (
            <div
              id="answer-char-counter"
              className="slds-grid slds-grid_align-spread slds-m-top_xx-small"
              data-testid="char-counter"
            >
              <div>
                {answerText.length >= MAX_ANSWER_LENGTH && (
                  <span className="slds-text-color_error slds-text-body_small" data-testid="char-limit-warning">
                    {tQuestion("character_limit_reached")}
                  </span>
                )}
                {answerText.trim().length < MIN_ANSWER_LENGTH && (
                  <span className="slds-text-body_small slds-text-color_weak" data-testid="min-chars-hint">
                    {tQuestion("min_chars_hint", { count: MIN_ANSWER_LENGTH })}
                  </span>
                )}
              </div>
              <span className="slds-text-body_small slds-text-color_weak" data-testid="char-counter-text">
                {tQuestion("character_counter", {
                  current: answerText.length,
                  limit: MAX_ANSWER_LENGTH,
                })}
              </span>
              {answerText.length >= MAX_ANSWER_LENGTH - 100 && (
                <span aria-live="polite" className="th-sr-only" data-testid="char-counter-live">
                  {tQuestion("chars_remaining", { count: MAX_ANSWER_LENGTH - answerText.length })}
                </span>
              )}
            </div>
          )}

          {!isAnswered && !submitting && <SubmitSection onSubmitAnswer={handleSubmitAnswer} answerText={answerText} />}
        </form>

        {(submitting || isAnswered) && (
          <PostSubmitSection
            submitting={submitting}
            response={isAnswered ? currentQuestion.response : null}
            isCompleted={isCompleted}
            onNextQuestion={onNextQuestion}
            feedbackHeadingRef={feedbackHeadingRef}
          />
        )}
      </div>
    </>
  );
};

QuestionScreen.propTypes = {
  attempt: PropTypes.shape({
    status: PropTypes.string.isRequired,
    questions: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  currentQuestion: PropTypes.shape({
    quizQuestionId: PropTypes.string.isRequired,
    questionText: PropTypes.string.isRequired,
    response: PropTypes.object,
  }).isRequired,
  currentQuestionIndex: PropTypes.number.isRequired,
  answerText: PropTypes.string.isRequired,
  submitting: PropTypes.bool.isRequired,
  onAnswerChange: PropTypes.func.isRequired,
  onSubmitAnswer: PropTypes.func.isRequired,
  onNextQuestion: PropTypes.func.isRequired,
  onBackToTrail: PropTypes.func.isRequired,
  onInfoClick: PropTypes.func.isRequired,
  onFeedbackClick: PropTypes.func,
  showFeedbackButton: PropTypes.bool,
};

export default QuestionScreen;
